// src/components/NoticeBoard.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  useTheme,
  IconButton,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion } from "framer-motion";
import axiosInstance from "../../lib/axios";

const NoticeBoard = () => {
  const theme = useTheme();
  const [notices, setNotices] = useState([]);
  const [displayedNotices, setDisplayedNotices] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [openCarousel, setOpenCarousel] = useState({
    open: false,
    files: [],
    startIndex: undefined,
    sourceNoticeId: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API CALL USING AXIOS
  useEffect(() => {
    const fetchNotices = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.post(
          "TrnNtcbrd/GetTrnNtcbrdDashBoard",
          {
            "start": 1,
            "PageSize": 1,
            "SearchText": ""
          },
        );

        const json = response.data;

        if (json.Status !== 0) {
          throw new Error(json.Message || "API returned error");
        }

        const rawData = json.Data || [];

        const transformedNotices = rawData.map((item) => {
          const dateStr = item.NtcbrdDt.split("T")[0];
          const [year, month, day] = dateStr.split("-");
          const formattedDate = `${year}-${month}-${day}`;

          let attachments = [];
          if (item.Attachment && item.Attachment.trim() !== "") {
            try {
              const urls = JSON.parse(item.Attachment);
              attachments = urls.map((url) => {
                const isPdf = url.toLowerCase().endsWith(".pdf");
                const name = url.split("/").pop() || (isPdf ? "document.pdf" : "image.jpg");
                return {
                  type: isPdf ? "pdf" : "image",
                  url,
                  name,
                  size: null,
                };
              });
            } catch (e) {
              console.warn("Failed to parse Attachment JSON", item.Attachment);
            }
          }

          return {
            id: item.NtcbrdNo,
            title: item.NtcbrdSubj || "No Title",
            date: formattedDate,
            time: "00:00 PM", // fallback
            content: item.NtcbrdDesc || "",
            hostel: item.PropName || "Property Name is not Coming",
            attachments,
          };
        });

        setNotices(transformedNotices);
        setDisplayedNotices(transformedNotices.slice(0, 3));
      } catch (err) {
        setError(err.response?.data?.Message || err.message || "Network error");
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const handleLoadMore = () => {
    setShowAll(true);
    setDisplayedNotices(notices);
  };

  const toggleReadMore = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ color: "#d32f2f", fontSize: 18 }} />;
      case "image":
        return <ImageIcon sx={{ color: "#1976d2", fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const openMediaCarousel = (files, startIndex, noticeId) => {
    const mediaFiles = files.filter((f) => f.type === "image" || f.type === "pdf");
    if (mediaFiles.length === 0) return;

    setOpenCarousel({
      open: true,
      files: mediaFiles,
      startIndex,
      sourceNoticeId: noticeId,
    });
  };

  const toggleAttachmentsList = (noticeId, files) => {
    const isCurrentlyOpen =
      openCarousel.sourceNoticeId === noticeId &&
      openCarousel.open &&
      openCarousel.startIndex === undefined;

    if (isCurrentlyOpen) {
      setOpenCarousel({ open: false, files: [], startIndex: undefined, sourceNoticeId: null });
    } else {
      setOpenCarousel({
        open: true,
        files,
        startIndex: undefined,
        sourceNoticeId: noticeId,
      });
    }
  };

  // Loading
  if (loading) {
    return (
      <Box sx={{ height: "85vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error
  if (error) {
    return (
      <Box sx={{ height: "85vh", display: "flex", alignItems: "center", justifyContent: "center", px: 3 }}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  // Empty
  if (!displayedNotices.length) {
    return (
      <Box sx={{ height: "85vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <AnnouncementIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ ml: 2 }}>No notices available</Typography>
      </Box>
    );
  }

 return (
    <Box sx={{
      minHeight: "85vh",
      px: { xs: 2, sm: 3, md: 2.5, lg: 3 }, py: { xs: 3, sm: 4 }, ml: { xs: 0, sm: 7, md: 0 }
    }}>
      {/* Header */}
      <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AnnouncementIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.primary.main }} />
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: "1.8rem", sm: "2.2rem" } }}>
              Notice/Announcement
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 5.5, mt: 0.5 }}>
            Stay updated with the latest announcements
          </Typography>
        </Box>
      </motion.div>

      {/* Cards Grid */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {displayedNotices.map((notice, index) => {
          const isExpandable = notice.content.length > 100;
          const isExpanded = expandedIds[notice.id];
          const shortContent = notice.content.slice(0, 100) + (isExpandable ? "..." : "");

          const isAttachmentListOpen =
            openCarousel.sourceNoticeId === notice.id &&
            openCarousel.open &&
            openCarousel.startIndex === undefined;

          return (
            <Grid item xs={12} sm={6} lg={4} key={notice.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background: "#fafafa",
                    border: "2px solid #e0e0e0",
                    borderRadius: 3,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px rgba(0,0,0,0.12)" },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    {/* Top: Notice # & Date/Time */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          color: [
                            "#d32f2f",
                            "#1976d2",
                            "#388e3c",
                            "#f57c00",
                            "#7b1fa2",
                            "#00796b",
                          ][index % 6],
                        }}
                      >
                        Notice #{notice.id}
                      </Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <AccessTimeIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.5 }} />
                          {notice.date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notice.time}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Title */}
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, fontSize: { xs: "1.1rem", sm: "1.2rem" } }}>
                      {notice.title}
                    </Typography>

                    {/* Content */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        flexGrow: 1,
                        lineHeight: 1.6,
                        display: "-webkit-box",
                        WebkitLineClamp: isExpanded ? "unset" : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {isExpanded ? notice.content : shortContent}
                    </Typography>

                    {isExpandable && (
                      <Button
                        size="small"
                        onClick={() => toggleReadMore(notice.id)}
                        sx={{ alignSelf: "flex-start", mt: 1, textTransform: "none", fontWeight: 600 }}
                      >
                        {isExpanded ? "Read Less" : "Read More"}
                      </Button>
                    )}

                    {/* Attachments Section */}
                    <Box sx={{ mt: 2 }}>
                      <Box
                        onClick={() => toggleAttachmentsList(notice.id, notice.attachments)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          cursor: "pointer",
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "rgba(0,0,0,0.05)",
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "0.85rem" }}>
                          Attachments ({notice.attachments.length})
                        </Typography>
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 20,
                            transform: isAttachmentListOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      </Box>

                      {isAttachmentListOpen && notice.attachments.length > 0 && (
                        <Box sx={{ mt: 1, pl: 2 }}>
                          {notice.attachments.map((file, idx) => (
                            <Box
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                openMediaCarousel(notice.attachments, idx, notice.id);
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                py: 0.75,
                                px: 1,
                                borderRadius: 1,
                                cursor: "pointer",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                              }}
                            >
                              {getFileIcon(file.type)}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.85rem",
                                  flex: 1,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {file.name}
                              </Typography>
                              {file.size && (
                                <Typography variant="caption" color="text.secondary">
                                  ({file.size})
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      {isAttachmentListOpen && notice.attachments.length === 0 && (
                        <Box sx={{ mt: 1, pl: 2, py: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", fontSize: "0.8rem" }}>
                            No attachments available.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Footer */}
                    <Box
                      sx={{
                        mt: "auto",
                        pt: 2,
                        borderTop: "1px dashed #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "0.8rem",
                      }}
                    >
                      <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: theme.palette.primary.main }}>
                        {notice.hostel[0] || "H"}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {notice.hostel}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>

      {/* Load More */}
      {!showAll && notices.length > displayedNotices.length && (
        <Box sx={{ mt: 5, textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ChevronRightIcon />}
            onClick={handleLoadMore}
            sx={{
              borderRadius: 3,
              px: { xs: 4, sm: 6 },
              py: 1.5,
              width: { xs: "100%", sm: "auto" },
              maxWidth: 400,
            }}
          >
            Load More Notices
          </Button>
        </Box>
      )}

      {/* Media Carousel Dialog */}
      <Dialog
        open={openCarousel.open && openCarousel.startIndex !== undefined}
        onClose={() => setOpenCarousel({ open: false, files: [], startIndex: undefined, sourceNoticeId: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: "#000", position: "relative" }}>
          <IconButton
            onClick={() => setOpenCarousel({ open: false, files: [], startIndex: undefined, sourceNoticeId: null })}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(255,255,255,0.9)",
              zIndex: 10,
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ position: "relative", height: "80vh" }}>
            {openCarousel.files.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: idx === openCarousel.startIndex ? "flex" : "none",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                {file.type === "image" ? (
                  <img
                    src={file.url}
                    alt={file.name}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : file.type === "pdf" ? (
                  <Box sx={{ color: "#fff", textAlign: "center" }}>
                    <PictureAsPdfIcon sx={{ fontSize: 80, mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, maxWidth: 500, mx: "auto" }}>
                      {file.name}
                    </Typography>
                    {file.size && (
                      <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                        {file.size}
                      </Typography>
                    )}
                    <Button
                      href={file.url}
                      target="_blank"
                      rel="noopener"
                      variant="contained"
                      color="error"
                      startIcon={<DownloadIcon />}
                      sx={{ mt: 1 }}
                    >
                      Download PDF
                    </Button>
                  </Box>
                ) : null}
              </Box>
            ))}

            {openCarousel.files.length > 1 && (
              <>
                <IconButton
                  onClick={() =>
                    setOpenCarousel((prev) => ({
                      ...prev,
                      startIndex: (prev.startIndex - 1 + prev.files.length) % prev.files.length,
                    }))
                  }
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <ChevronRightIcon sx={{ transform: "rotate(180deg)" }} />
                </IconButton>

                <IconButton
                  onClick={() =>
                    setOpenCarousel((prev) => ({
                      ...prev,
                      startIndex: (prev.startIndex + 1) % prev.files.length,
                    }))
                  }
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NoticeBoard;